import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { useOrdersSocket } from "../hooks/useOrdersSocket";
import { STATUS_ORDER, STATUS_LABELS, WA_STATUS_LABELS, orderItems, itemsSummary, isOverdue } from "../lib/constants";
import { toast } from "sonner";
import { Wifi, WifiOff, Search, Phone, Truck, Calendar, AlertTriangle, Mail, RotateCw, Pencil, PackageCheck, UserCheck, Download, FileText, Trash2, Clock, Archive } from "lucide-react";
import { OrderEditModal } from "../components/OrderEditModal";
import { DeliverModal } from "../components/DeliverModal";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toCSV, downloadCSV } from "../lib/csv";
import { openSevkFormu } from "../lib/sevkForm";

const WA_COLOR = {
  success: "text-emerald-600", failed: "text-rose-600", pending: "text-amber-600",
  sending: "text-amber-600", not_applicable: "text-slate-400",
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const isArchived = (o) => o.status === "delivered" && o.archived !== false;
  const [search, setSearch] = useState("");
  const [editOrder, setEditOrder] = useState(null);
  const [deliverOrder, setDeliverOrder] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [deleteOrder, setDeleteOrder] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/orders");
      setOrders(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Live fallback: poll periodically so the list stays current even if the
  // WebSocket is blocked or (in production) served by a different replica.
  useEffect(() => {
    const t = setInterval(() => load(), 12000);
    return () => clearInterval(t);
  }, [load]);

  const onWs = useCallback((msg) => {
    if (msg.type === "reconnected") { load(); return; }
    if (msg.type === "order_deleted") {
      setOrders((prev) => prev.filter((o) => o.id !== msg.order_id));
      return;
    }
    if (msg.type === "order_created" || msg.type === "order_updated") {
      setOrders((prev) => {
        const idx = prev.findIndex((o) => o.id === msg.order.id);
        if (idx === -1) return [msg.order, ...prev];
        const copy = [...prev];
        copy[idx] = msg.order;
        return copy;
      });
    }
  }, [load]);

  const { connected } = useOrdersSocket(onWs);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/orders/${id}/status`, { status });
      toast.success("Durum güncellendi");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const resend = async (id, order_no) => {
    try {
      const { data } = await api.post(`/orders/${id}/resend-whatsapp`);
      if (data.whatsapp_status === "success") toast.success(`#${order_no} E-posta gönderildi ✓`);
      else toast.error(`#${order_no} tekrar başarısız — personele uyarı iletildi`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const confirmDelete = async () => {
    const o = deleteOrder;
    if (!o) return;
    try {
      await api.delete(`/orders/${o.id}`);
      setOrders((prev) => prev.filter((x) => x.id !== o.id));
      toast.success(`#${o.order_no} silindi`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setDeleteOrder(null);
    }
  };

  const archive = async (o) => {
    try {
      await api.post(`/orders/${o.id}/archive`);
      setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, archived: true } : x)));
      toast.success(`#${o.order_no} arşivlendi`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const restore = async (o) => {
    try {
      await api.post(`/orders/${o.id}/restore`);
      setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, archived: false } : x)));
      toast.success(`#${o.order_no} geri alındı`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const filtered = orders
    .filter((o) => (filter === "archived" ? isArchived(o) : !isArchived(o) && (filter === "all" || o.status === filter)))
    .filter((o) => {
      if (!dateFrom && !dateTo) return true;
      const d = (o.created_at || "").slice(0, 10);
      if (dateFrom && d < dateFrom) return false;
      if (dateTo && d > dateTo) return false;
      return true;
    })
    .filter((o) => {
      if (!search) return true;
      const s = search.toLowerCase();
      return (
        o.order_no?.toLowerCase().includes(s) ||
        o.customer_snapshot?.company_name?.toLowerCase().includes(s) ||
        o.product_snapshot?.product_code?.toLowerCase().includes(s) ||
        o.customer_snapshot?.phone?.includes(s)
      );
    });

  const exportCSV = () => {
    const rows = filtered.map((o) => ({
      order_no: o.order_no,
      company: o.customer_snapshot?.company_name || "",
      phone: o.customer_snapshot?.phone || "",
      items: orderItems(o).map((it) => `${it.product_code} ${it.product_name} ${it.quantity}${it.unit}`).join(" | "),
      status: STATUS_LABELS[o.status] || o.status,
      shipping: o.shipping_type || "",
      delivery_date: o.delivery_date || "",
      whatsapp: WA_STATUS_LABELS[o.whatsapp_status] || "",
      received_by: o.received_by || "",
      delivered_at: o.delivered_at ? new Date(o.delivered_at).toLocaleString("tr-TR") : "",
      created_at: o.created_at ? new Date(o.created_at).toLocaleString("tr-TR") : "",
      note: o.note || "",
    }));
    const cols = [
      ["order_no", "Sipariş No"], ["company", "Firma"], ["phone", "Telefon"], ["items", "Ürünler"],
      ["status", "Durum"], ["shipping", "Sevk"], ["delivery_date", "Teslim Tarihi"], ["whatsapp", "E-posta Bildirimi"],
      ["received_by", "Teslim Alan"], ["delivered_at", "Teslim Zamanı"], ["created_at", "Oluşturma"], ["note", "Not"],
    ];
    if (!rows.length) { toast.error("Dışa aktarılacak sipariş yok"); return; }
    downloadCSV(toCSV(cols, rows), `siparisler_${new Date().toISOString().slice(0, 10)}.csv`);
    toast.success(`${rows.length} sipariş dışa aktarıldı`);
  };

  const exportArchivePDF = () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("token");
    const qs = new URLSearchParams();
    if (dateFrom) qs.set("start", dateFrom);
    if (dateTo) qs.set("end", dateTo);
    qs.set("token", token || "");
    window.open(`${base}/api/orders/archive.pdf?${qs.toString()}`, "_blank");
  };

  const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const quickRange = (kind) => {
    const now = new Date();
    let from = new Date(now);
    if (kind === "week") from.setDate(now.getDate() - ((now.getDay() + 6) % 7)); // haftanın başı (Pazartesi)
    else if (kind === "month") from = new Date(now.getFullYear(), now.getMonth(), 1);
    setDateFrom(fmtDate(from));
    setDateTo(fmtDate(now));
  };

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Siparişler</h1>
          <p className="text-sm text-muted-foreground">{filtered.length} sipariş listeleniyor</p>
        </div>
        <ConnPill connected={connected} />
      </div>

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="orders-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Sipariş no, firma, kod, telefon ara..."
            className="w-full rounded-xl border border-border bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-primary"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {[["today", "Bugün"], ["week", "Bu Hafta"], ["month", "Bu Ay"]].map(([k, label]) => (
            <button key={k} data-testid={`orders-quick-${k}`} onClick={() => quickRange(k)}
              className="rounded-lg border border-border bg-white px-3 py-2 text-sm font-medium hover:border-primary hover:bg-primary/5">
              {label}
            </button>
          ))}
          <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
            Başlangıç
            <input data-testid="orders-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="rounded-lg border border-border bg-white px-2.5 py-2 text-sm outline-none focus:border-primary" />
          </label>
          <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
            Bitiş
            <input data-testid="orders-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="rounded-lg border border-border bg-white px-2.5 py-2 text-sm outline-none focus:border-primary" />
          </label>
          {(dateFrom || dateTo) && (
            <button data-testid="orders-date-clear" onClick={() => { setDateFrom(""); setDateTo(""); }}
              className="rounded-lg border border-border bg-white px-3 py-2 text-sm font-medium hover:bg-secondary">Temizle</button>
          )}
          <button data-testid="orders-export-btn" onClick={exportCSV}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-white hover:brightness-110">
            <Download className="h-4 w-4" /> Dışa Aktar
          </button>
          {filter === "archived" && (
            <button data-testid="orders-archive-pdf-btn" onClick={exportArchivePDF}
              className="flex items-center gap-1.5 rounded-lg border border-primary bg-white px-3.5 py-2 text-sm font-semibold text-primary hover:bg-primary/5">
              <FileText className="h-4 w-4" /> Arşiv PDF
            </button>
          )}
        </div>
      </div>

      <div className="mb-5 flex flex-wrap gap-2">
        {["all", ...STATUS_ORDER.filter((s) => s !== "preparing")].map((s) => (
          <button
            key={s}
            data-testid={`filter-${s}`}
            onClick={() => setFilter(s)}
            className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
              filter === s ? "border-primary bg-primary text-white" : "border-border bg-white hover:bg-secondary"
            }`}
          >
            {s === "all" ? "Tüm Durumlar" : STATUS_LABELS[s]}
          </button>
        ))}
        <button
          data-testid="filter-archived"
          onClick={() => setFilter("archived")}
          className={`flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
            filter === "archived" ? "border-primary bg-primary text-white" : "border-border bg-white hover:bg-secondary"
          }`}
        >
          <Archive className="h-3.5 w-3.5" /> Arşiv
        </button>
      </div>

      <div className="space-y-3" data-testid="orders-list">
        {filtered.length === 0 && (
          <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">
            Sipariş bulunamadı.
          </div>
        )}
        {filtered.map((o) => (
          <div key={o.id} data-testid={`order-row-${o.order_no}`} className="rounded-2xl border border-border bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2.5">
                  <span className="font-mono text-sm font-bold text-primary">{o.order_no}</span>
                  <StatusBadge status={o.status} />
                  {isOverdue(o) && (
                    <span data-testid={`order-overdue-${o.order_no}`} className="inline-flex items-center gap-1 rounded-full bg-rose-500 px-2 py-0.5 text-xs font-bold text-white">
                      <Clock className="h-3 w-3" /> Gecikti
                    </span>
                  )}
                  {o.whatsapp_status === "failed" && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/10 px-2 py-0.5 text-xs font-medium text-rose-600">
                      <AlertTriangle className="h-3 w-3" /> E-posta hatası
                    </span>
                  )}
                </div>
                <div className="mt-1.5 font-display text-lg font-bold">{o.customer_snapshot?.company_name}</div>
                <div className="mt-1 space-y-0.5 text-sm text-muted-foreground">
                  {orderItems(o).map((it, i) => (
                    <div key={i}>
                      <span className="font-mono font-semibold text-foreground">{it.product_code}</span>{" "}
                      {it.product_name} · <span className="font-semibold text-foreground">{it.quantity} {it.unit}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-1 text-xs font-semibold text-primary">
                  {itemsSummary(o).count} kalem{itemsSummary(o).totals ? ` · Toplam: ${itemsSummary(o).totals}` : ""}
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{o.customer_snapshot?.phone}</span>
                  {o.shipping_type && <span className="flex items-center gap-1"><Truck className="h-3 w-3" />{o.shipping_type}</span>}
                  {o.delivery_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{o.delivery_date.split("-").reverse().join(".")}</span>}
                  <span className={`flex items-center gap-1 ${WA_COLOR[o.whatsapp_status]}`}>
                    <Mail className="h-3 w-3" />E-posta: {WA_STATUS_LABELS[o.whatsapp_status]}
                  </span>
                </div>
                {o.status === "delivered" && (
                  <div data-testid={`order-delivered-info-${o.order_no}`} className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-emerald-500/10 px-3 py-1.5 text-xs font-medium text-emerald-700">
                    <span className="flex items-center gap-1"><PackageCheck className="h-3.5 w-3.5" />Teslim: {o.delivered_at ? new Date(o.delivered_at).toLocaleString("tr-TR") : "-"}</span>
                    {o.received_by && <span className="flex items-center gap-1"><UserCheck className="h-3.5 w-3.5" />Teslim Alan: {o.received_by}</span>}
                  </div>
                )}
                {o.note && <div className="mt-2 rounded-lg bg-secondary px-3 py-1.5 text-xs text-muted-foreground">Not: {o.note}</div>}
              </div>
              <div className="flex flex-col items-stretch gap-2">
                <select
                  data-testid={`order-status-select-${o.order_no}`}
                  value={o.status}
                  onChange={(e) => (e.target.value === "delivered" ? setDeliverOrder(o) : changeStatus(o.id, e.target.value))}
                  className="rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-primary"
                >
                  {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                </select>
                <button
                  data-testid={`order-edit-${o.order_no}`}
                  onClick={() => setEditOrder(o)}
                  className="flex items-center justify-center gap-1.5 rounded-lg border border-border bg-white px-3 py-2 text-sm font-semibold hover:bg-secondary"
                >
                  <Pencil className="h-3.5 w-3.5" /> Düzenle
                </button>
                {(o.status === "ready" || o.status === "notified") && (
                  <button
                    data-testid={`order-deliver-${o.order_no}`}
                    onClick={() => setDeliverOrder(o)}
                    className="flex items-center justify-center gap-1.5 rounded-lg bg-cyan-500 px-3 py-2 text-sm font-semibold text-white hover:bg-cyan-400"
                  >
                    <PackageCheck className="h-3.5 w-3.5" /> Teslim Et
                  </button>
                )}
                {o.status === "delivered" && (
                  <button
                    data-testid={`order-sevk-${o.order_no}`}
                    onClick={() => openSevkFormu(o)}
                    className="flex items-center justify-center gap-1.5 rounded-lg border border-cyan-500 bg-white px-3 py-2 text-sm font-semibold text-cyan-600 hover:bg-cyan-50"
                  >
                    <FileText className="h-3.5 w-3.5" /> Sevk Formu
                  </button>
                )}
                {o.whatsapp_status === "failed" && (
                  <button
                    data-testid={`order-resend-${o.order_no}`}
                    onClick={() => resend(o.id, o.order_no)}
                    className="flex items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white hover:brightness-110"
                  >
                    <RotateCw className="h-3.5 w-3.5" /> Yeniden Gönder
                  </button>
                )}
                {isArchived(o) ? (
                  <button
                    data-testid={`order-restore-${o.order_no}`}
                    onClick={() => restore(o)}
                    className="flex items-center justify-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
                  >
                    <RotateCw className="h-3.5 w-3.5" /> Geri Al
                  </button>
                ) : o.status === "delivered" ? (
                  <button
                    data-testid={`order-archive-${o.order_no}`}
                    onClick={() => archive(o)}
                    className="flex items-center justify-center gap-1.5 rounded-lg border border-border bg-white px-3 py-2 text-sm font-semibold text-muted-foreground hover:bg-secondary"
                  >
                    <Archive className="h-3.5 w-3.5" /> Arşivle
                  </button>
                ) : null}
                <button
                  data-testid={`order-delete-${o.order_no}`}
                  onClick={() => setDeleteOrder(o)}
                  className="flex items-center justify-center gap-1.5 rounded-lg border border-rose-300 bg-white px-3 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Sil
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editOrder && <OrderEditModal order={editOrder} onClose={() => setEditOrder(null)} onSaved={() => { setEditOrder(null); load(); }} />}
      {deliverOrder && <DeliverModal order={deliverOrder} onClose={() => setDeliverOrder(null)} onDone={() => { setDeliverOrder(null); load(); }} />}

      <AlertDialog open={!!deleteOrder} onOpenChange={(open) => { if (!open) setDeleteOrder(null); }}>
        <AlertDialogContent data-testid="order-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Siparişi sil?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-mono font-bold">{deleteOrder?.order_no}</span> · {deleteOrder?.customer_snapshot?.company_name} siparişi kalıcı olarak silinecek. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="order-delete-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="order-delete-confirm" onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function ConnPill({ connected }) {
  return (
    <span
      data-testid="ws-connection-status-pill"
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${
        connected ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600" : "border-rose-500/40 bg-rose-500/10 text-rose-600"
      }`}
    >
      {connected ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
      {connected ? "CANLI BAĞLANTI" : "BAĞLANTI KOPUK"}
    </span>
  );
}
